import React, { useState } from 'react';
import { useUserProfile } from '@/services/user/hooks';
import { ROLES } from '@/constants/roles';
import { authService } from '@/services/auth/authService';
import { useCreditBalance } from '@/services/credits/hooks';
import { useTheme } from '@/contexts/ThemeContext';
import { currentVersion } from '@/constants/changelog';
import { useChangelogUnseen } from '@/hooks/useChangelogUnseen';
import { LogOut, Moon, Sun, ChevronDown, Gift } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import WhatsNewDialog from './WhatsNewDialog';

const UserMenu: React.FC = () => {
  const userProfile = useUserProfile();
  const isAdmin = userProfile.roles?.includes(ROLES.ADMIN) ?? false;
  const { data: balance } = useCreditBalance();
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const { unseenCount, markAllSeen } = useChangelogUnseen();

  const openWhatsNew = () => {
    setIsOpen(false);
    setWhatsNewOpen(true);
    markAllSeen();
  };

  const handleLogout = () => {
    setIsOpen(false);
    authService.logout();
  };

  const getUserInitials = () => {
    if (userProfile.name) {
      const parts = userProfile.name.split(' ');
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      return userProfile.name[0].toUpperCase();
    }
    return userProfile.username?.[0].toUpperCase() ?? 'U';
  };

  const totalCredits = balance?.totalAvailable || 0;

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-auto px-2 py-1.5 gap-2">
            <div className="relative">
              <Avatar className="size-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              {totalCredits > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {totalCredits}
                </span>
              )}
              {unseenCount > 0 && (
                <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-primary ring-2 ring-background" />
              )}
            </div>
            <div className="hidden sm:flex flex-col items-start">
              <span className="text-sm font-medium text-foreground">
                {userProfile.name || userProfile.username || 'User'}
              </span>
              <div className="flex gap-1">
                {isAdmin && (
                  <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4">
                    Admin
                  </Badge>
                )}
                {userProfile.teamName && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                    {userProfile.teamName}
                  </Badge>
                )}
              </div>
            </div>
            <ChevronDown className="size-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-70">
          {/* User Info Header */}
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-center gap-3">
              <Avatar className="size-10">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="font-semibold">{userProfile.name || userProfile.username}</span>
                <span className="text-xs text-muted-foreground">{userProfile.email ?? ''}</span>
                {userProfile.teamName && (
                  <span className="text-xs text-muted-foreground">{userProfile.teamName}</span>
                )}
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={toggleTheme}>
            {theme === 'light' ? <Moon className="size-4" /> : <Sun className="size-4" />}
            <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={openWhatsNew}>
            <Gift className="size-4" />
            <span>What&apos;s new</span>
            {unseenCount > 0 && (
              <Badge variant="default" className="ml-auto h-4 px-1.5 py-0 text-[10px]">
                {unseenCount}
              </Badge>
            )}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={handleLogout}
            className="text-destructive focus:text-destructive"
          >
            <LogOut className="size-4" />
            <span>Logout</span>
          </DropdownMenuItem>

          {currentVersion && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1 text-center text-[11px] text-muted-foreground">
                Version {currentVersion}
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <WhatsNewDialog open={whatsNewOpen} onOpenChange={setWhatsNewOpen} />
    </>
  );
};

export default UserMenu;
